import { AnyID, Item, Partition, Setter, Walls } from '@types'
import { quatYToDeg } from '@utils/conversion/quatYToDeg'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import decomposeMatrix from '@utils/decomposeMatrix'
import { radians } from '@utils/generalMaths/geometry'
import isPartition from '@utils/sceneObjects/assertions/isPartition'
import getId from '@utils/sceneObjects/getId'
import { addItemOffset } from '@utils/sceneObjects/items/addItemOffset'
import { getBasePartition } from '@utils/sceneObjects/partitions/getBasePartition'
import { projectItemOnSlabFaces } from '@utils/sceneObjects/projectItemOnSlabFaces'
import { getTouchedFace } from '@utils/sceneObjects/snappedSides'
import { Euler, Matrix4, Quaternion, Vector3 } from 'three'

export const createPartitionsActions = (set: Setter) => ({
  drawPartition: (heights?: number) => {
    set((store) => {
      const height = !heights ? store.prefs.ALTOPARED : heights
      const width = 1500
      const thickness = 100
      const partitionID = getId('partition')
      const position = toVector3([0, height / 2, 0])

      const newMatrix = new Matrix4()
      newMatrix.makeRotationY(radians(-180)).setPosition(position)

      store.items.set(
        partitionID,
        getBasePartition(
          {
            showAngle: false,
            visible: true,
            thickness: thickness,
            rotation: 0,
            x: 0,
            z: 0,
            id: partitionID,
            height,
            width: width,
            matrix: newMatrix,
          },
          store,
        ),
      )
      store.layers[store.currentLayer].items.add(partitionID)
      store.cameraCenter = [0, 4000, 0]
      store.currentObjectID = partitionID
      store.select(partitionID, false)
    })
  },
  editPartitionProperty: <T extends Partition>(
    id: string,
    config?: Partial<Record<keyof T, T[keyof T]>>,
  ) => {
    set((store) => {
      const item = store.items.get(id)
      if (!config) return
      for (const [property, value] of Object.entries(config)) {
        if (value instanceof Matrix4) {
          item[property] = value.clone()
          continue
        }
        item[property] = value
      }
    })
  },
  editPartitionAngle: (id: string, angle: number) => {
    set((store) => {
      const partition = store.items.get(id) as Partition
      const position = toVector3()

      partition.matrix.decompose(position, new Quaternion(), toVector3())
      partition.matrix.makeRotationY(radians(-angle))
      partition.matrix.setPosition(position)
    })
  },
  editPartitionAngleX: (id: string, angle: number) => {
    set((store) => {
      const partition = store.items.get(id) as Partition
      const position = toVector3()
      if (!isPartition(partition)) return
      partition.matrix.decompose(position, new Quaternion(), toVector3())
      partition.matrix.makeRotationX(radians(-angle))
      partition.matrix.setPosition(position)
    })
  },
  editPartitionAngleZ: (id: string, angle: number) => {
    set((store) => {
      const partition = store.items.get(id) as Partition
      const position = toVector3()
      if (!isPartition(partition)) return
      partition.matrix.decompose(position, new Quaternion(), toVector3())
      partition.matrix.makeRotationZ(radians(-angle))
      partition.matrix.setPosition(position)
    })
  },
  drawPartitionFromWall: (id: string) => {
    set((store) => {
      const wall = store.items.get(id) as Walls | Partition
      const height = wall.height
      const width = 1000
      const thickness = 100
      const partitionID = getId('partition')
      const position = toVector3()

      const rotation = wall.rotation
      const x =
        wall.x -
        ((width / 2 + wall.thickness / 2) * Math.sin(rotation) +
          (wall.thickness / 2) * Math.cos(rotation))
      const z =
        wall.z +
        ((width / 2 + wall.thickness / 2) * Math.cos(rotation) -
          (wall.thickness / 2) * Math.sin(rotation))

      const newMatrix = new Matrix4().compose(
        toVector3([x, height / 2, z]),
        toQuaternion().setFromEuler(
          new Euler().setFromVector3(toVector3([0, getRightPerpendicular(-rotation), 0])),
        ),
        toVector3([1, 1, 1]),
      )
      newMatrix.decompose(position, toQuaternion(), toVector3())
      function getRightPerpendicular(angle: number) {
        const perpendicularAngle = angle - Math.PI / 2
        return perpendicularAngle
      }
      store.items.set(
        partitionID,
        getBasePartition(
          {
            showAngle: false,
            visible: true,
            thickness: thickness,
            rotation: 0,
            x: x + (width / 2) * -Math.sin(rotation),
            z: z + (thickness / 2) * Math.cos(rotation),
            id: partitionID,
            height,
            width: width,
            matrix: newMatrix,
          },
          store,
        ),
      )
      store.layers[store.currentLayer].items.add(partitionID)
      store.currentObjectID = partitionID
    })
  },
  drawPartitionFromPartition: (id: string) => {
    set((store) => {
      const wall = store.items.get(id) as Partition
      const height = wall.height
      const width = 1000
      const thickness = 100
      const partitionID = getId('partition')
      const position = toVector3()

      const rotation = decomposeMatrix(wall.matrix).rotation
      const rot = radians(quatYToDeg(rotation))
      const x =
        wall.x -
        ((width + wall.thickness / 2) * Math.sin(rot) + (wall.thickness / 2) * Math.cos(rot))
      const z =
        wall.z +
        ((width / 2 + wall.thickness / 2) * Math.cos(rot) - (wall.thickness / 2) * Math.sin(rot))

      const newMatrix = new Matrix4().compose(
        toVector3([x, height / 2, z]),
        toQuaternion().setFromEuler(
          new Euler().setFromVector3(toVector3([0, getRightPerpendicular(-rot), 0])),
        ),
        toVector3([1, 1, 1]),
      )
      newMatrix.decompose(position, toQuaternion(), toVector3())
      function getRightPerpendicular(angle: number) {
        const perpendicularAngle = angle - Math.PI / 2
        return perpendicularAngle
      }
      store.items.set(
        partitionID,
        getBasePartition(
          {
            showAngle: false,
            visible: true,
            thickness: thickness,
            rotation: 0,
            x: x + (width / 2) * -Math.sin(rot),
            z: z + (thickness / 2) * Math.cos(rot),
            id: partitionID,
            height,
            width: width,
            matrix: newMatrix,
          },
          store,
        ),
      )
      store.layers[store.currentLayer].items.add(partitionID)
      store.currentObjectID = partitionID
      store.select(partitionID)
    })
  },
  projectPartitionOnWall: (partitionID: string, wallID: string, matrix: Matrix4) => {
    set((store) => {
      const wall = store.items.get(wallID)
      const partition = store.items.get(partitionID) as Partition
      const partitionWidth = partition.width
      if (!wall) return
      const { rotation, thickness } = wall
      const partitionPosition = toVector3()
      matrix.decompose(partitionPosition, toQuaternion(), toVector3())
      const end = toVector3(wall.end)
      const start = toVector3(wall.start)
      const wallDirection = end.clone().sub(start)
      const partitionToStart = partitionPosition.clone().sub(start)
      const projectedVector = partitionToStart.projectOnVector(wallDirection.clone().normalize())
      const adjustedPosition = start
        .clone()
        .add(wallDirection.normalize().multiplyScalar(projectedVector.length()))
      const finalX = adjustedPosition.x - (partitionWidth / 2) * Math.sin(rotation)
      const finalZ = adjustedPosition.z + (partitionWidth / 2) * Math.cos(rotation)
      const finalY =
        partitionPosition.y - partition.height / 2 < 0
          ? 0
          : partitionPosition.y - partition.height / 2
      function getRightPerpendicular(angle: number) {
        const perpendicularAngle = angle - Math.PI / 2
        return perpendicularAngle
      }

      if (!partition) return
      if (!partition.visible) partition.visible = true

      partition.matrix.makeRotationFromEuler(
        new Euler(0, getRightPerpendicular(-rotation), 0, 'XYZ'),
      )
      partition.matrix.setPosition(finalX, partitionPosition.y, finalZ)
      partition.x = finalX
      partition.y = finalY
      partition.z = finalZ
      partition.snappedWall = wallID
    })
  },
  editPartitionHeightFromFloor: (id: string, height: number) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, height + item.height / 2, pos.z)
      item.y = height
    })
  },
  incrementPartitionZ: (id: string, z: number) => {
    const axis = 'z'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()

      item.matrix = addItemOffset(item.matrix, z, axis)
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.y = pos.y - item.height / 2
      item.x = pos.x
      item.z = pos.z
    })
  },
  incrementPartitionY: (id: string, y: number) => {
    const axis = 'y'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()

      item.matrix = addItemOffset(item.matrix, y, axis)
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.y = pos.y - item.height / 2
      item.x = pos.x
      item.z = pos.z
    })
  },
  incrementPartitionX: (id: string, x: number) => {
    const axis = 'x'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()

      item.matrix = addItemOffset(item.matrix, x, axis)
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.y = pos.y - item.height / 2
      item.x = pos.x
      item.z = pos.z
    })
  },
  updateStartEndPartition: (id: string) => {
    set((store) => {
      const partition = store.items.get(id) as Partition
      if (!partition) return
      const matrix = partition.matrix
      const { rotation: rot, position: pos } = decomposeMatrix(matrix)
      const quatY = radians(quatYToDeg(rot))
      const endX =
        pos.x + (partition.width * Math.cos(-quatY) - partition.thickness * Math.sin(-quatY)) / 2
      const endZ =
        pos.z + (partition.width * Math.sin(-quatY) + partition.thickness * Math.cos(-quatY)) / 2
      const startX =
        pos.x - (partition.width * Math.cos(-quatY) + partition.thickness * Math.sin(-quatY)) / 2
      const startZ =
        pos.z - (partition.width * Math.sin(-quatY) - partition.thickness * Math.cos(-quatY)) / 2

      const endX1 =
        pos.x - (partition.width * Math.cos(-quatY) - partition.thickness * Math.sin(-quatY)) / 2
      const endZ1 =
        pos.z - (partition.width * Math.sin(-quatY) + partition.thickness * Math.cos(-quatY)) / 2
      const startX1 =
        pos.x + (partition.width * Math.cos(-quatY) + partition.thickness * Math.sin(-quatY)) / 2
      const startZ1 =
        pos.z + (partition.width * Math.sin(-quatY) - partition.thickness * Math.cos(-quatY)) / 2

      partition.start = [startX, startZ]
      partition.end = [endX, endZ]
      partition.start1 = [startX1, startZ1]
      partition.end1 = [endX1, endZ1]
    })
  },
  projectItemOnPartition: (itemID: string, partitionID: string, itemMatrix: Matrix4) => {
    set((store) => {
      const partition = store.items.get(partitionID) as Partition
      const item = store.items.get(itemID) as Item
      if (!partition || !item) return

      // La partición como OBB: la posición de su matriz es su CENTRO (no esquina),
      // X local a lo largo del ancho y Z local a través del grosor (ver
      // updateStartEndPartition: start/end y start1/end1 son sus dos caras largas).
      const partitionCenter = new Vector3()
      const partitionQuat = new Quaternion()
      partition.matrix.decompose(partitionCenter, partitionQuat, new Vector3())

      projectItemOnSlabFaces(
        store,
        itemID as AnyID,
        partitionID as AnyID,
        partition,
        itemMatrix,
        partitionQuat,
        partitionCenter,
        (partition.width || 0) / 2,
        false,
      )
    })
  },
  projectPartitionOnPartition: (itemID: string, partitionID: string, itemMatrix: Matrix4) => {
    set((store) => {
      if (!isPartition(partitionID) || !isPartition(itemID)) return

      const partition = store.items.get(partitionID) as Partition
      const item = store.items.get(itemID) as Partition
      if (!partition || !item) return

      const partitionCenter = new Vector3()
      const partitionQuat = new Quaternion()
      partition.matrix.decompose(partitionCenter, partitionQuat, new Vector3())

      const itemPosition = new Vector3()
      const itemQuat = new Quaternion()
      itemMatrix.decompose(itemPosition, itemQuat, new Vector3())

      // Snap por caras tipo pouff entre particiones: a diferencia de los items
      // (origen en esquina), la matriz de una partición ya apunta a su centro, así
      // que se usa directamente para elegir la cara y calcular el soporte. La
      // partición arrastrada NO rota: solo se desplaza por la normal de la cara
      // tocada hasta quedar a tope.
      const touched = getTouchedFace(
        partitionQuat,
        partitionCenter,
        (partition.width || 0) / 2,
        (partition.thickness || 0) / 2,
        itemPosition,
      )
      const faceNormal = touched.axis.clone().multiplyScalar(touched.sign)

      const itemXAxis = new Vector3(1, 0, 0).applyQuaternion(itemQuat)
      const itemZAxis = new Vector3(0, 0, 1).applyQuaternion(itemQuat)
      const itemExtent =
        ((item.width || 0) / 2) * Math.abs(itemXAxis.dot(faceNormal)) +
        ((item.thickness || 0) / 2) * Math.abs(itemZAxis.dot(faceNormal))

      const desiredProjection = touched.sign * (touched.extent + itemExtent)
      const correction = desiredProjection - touched.projection
      const newPosition = itemPosition.clone().add(touched.axis.clone().multiplyScalar(correction))

      item.matrix.makeRotationFromQuaternion(itemQuat)
      if (!item.visible) item.visible = true
      item.matrix.setPosition(newPosition.x, itemPosition.y, newPosition.z)
      item.x = newPosition.x
      item.z = newPosition.z
      // item.y es la base de la partición; su matriz guarda el centro
      item.y = Math.max(0, itemPosition.y - partition.height / 2)
      // ItemGroups no deben tener snappedWall
      if (item.type !== 'itemGroup') {
        item.snappedWall = partitionID
      }
    })
  },
})
